import type { AutomationDefinition, CreateAutomationInput } from '@hivett/shared';

export interface SessionState {
  token: string;
  expiresAt?: string;
  user?: {
    id?: string;
    account?: string;
  };
}

interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

export type BillingSummary = {
  payToAccount: string;
  adminAccounts: string[];
  isAdmin: boolean;
  creditsPerRun: number;
  runPricing: {
    mode: string;
    baseCredits: number;
    perActionCredits: number;
    maxCreditsPerRun: number;
    triggerTypeCredits: Record<string, number>;
    actionKindCredits: Record<string, number>;
  };
  memoToken: string;
  credits: {
    balanceCredits: number;
    totalPurchasedCredits: number;
    totalConsumedCredits: number;
  };
  pricing: {
    hiveUsd: number;
    hbdUsd: number;
    fetchedAt: string;
    expiresAt: string;
    source: string;
  } | null;
  plans: Array<{
    code: string;
    name: string;
    usdCents: number;
    credits: number;
    description?: string;
    paymentMemo: string;
    quote: { hive: string; hbd: string } | null;
  }>;
  recentPayments: Array<Record<string, unknown>>;
  recentLedger: Array<Record<string, unknown>>;
};

export const SESSION_STORAGE_KEY = 'hivett_session';
const AUTH_EVENT = 'hivett-auth-changed';
export const REQUEST_TIMEOUT_MS = 30_000;
const BILLING_SUMMARY_CACHE_MS = 10_000;
// Spread proactive refreshes across tabs so they rarely race each other.
const REFRESH_JITTER_MS = 5_000;

export class ApiClient {
  private readonly baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';
  private session: SessionState | null = this.loadSession();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private billingSummaryCache: { token: string; at: number; promise: Promise<BillingSummary> } | null = null;

  private readonly onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== SESSION_STORAGE_KEY) {
      return;
    }

    // Another tab signed in, signed out or rotated the token. Adopt its state
    // instead of keeping a token the server may already have revoked.
    const next = this.loadSession();
    if (next?.token === this.session?.token && next?.expiresAt === this.session?.expiresAt) {
      return;
    }

    this.applySession(next);
  };

  public constructor() {
    // A session restored from localStorage on reload must also re-arm its
    // proactive refresh timer, otherwise the JWT silently expires mid-session.
    if (this.session) {
      this.scheduleSessionRefresh();
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('storage', this.onStorage);
    }
  }

  public getSession() {
    return this.session;
  }

  public hasSession() {
    return Boolean(this.session?.token);
  }

  public onSessionChange(listener: (session: SessionState | null) => void) {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<SessionState | null>;
      listener(customEvent.detail ?? null);
    };

    window.addEventListener(AUTH_EVENT, handler);
    return () => window.removeEventListener(AUTH_EVENT, handler);
  }

  private loadSession(): SessionState | null {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as SessionState | null;
      return parsed && typeof parsed.token === 'string' && parsed.token ? parsed : null;
    } catch {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
  }

  private persistSession(session: SessionState | null) {
    if (!session) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }

    this.applySession(session);
  }

  /** Updates in-memory state and listeners without writing to storage. */
  private applySession(session: SessionState | null) {
    this.session = session;
    this.billingSummaryCache = null;

    if (!session) {
      this.cancelSessionRefresh();
    } else {
      this.scheduleSessionRefresh();
    }

    window.dispatchEvent(new CustomEvent<SessionState | null>(AUTH_EVENT, { detail: session }));
  }

  private scheduleSessionRefresh() {
    this.cancelSessionRefresh();

    if (!this.session?.expiresAt) {
      return;
    }

    const expiresMs = new Date(this.session.expiresAt).getTime() - Date.now();
    const refreshAt = Math.max(expiresMs * 0.8, 60_000) + Math.floor(Math.random() * REFRESH_JITTER_MS);

    this.refreshTimer = setTimeout(() => {
      void this.refreshSession();
    }, refreshAt);
  }

  private cancelSessionRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Exposed for tests; normally driven by the refresh timer. */
  public async refreshSession() {
    const current = this.session;
    if (!current?.token) {
      return;
    }

    // Another tab may already have rotated the token. If storage moved on,
    // adopt that session rather than refreshing (and revoking) it again.
    const stored = this.loadSession();
    if (!stored) {
      this.applySession(null);
      return;
    }

    if (stored.token !== current.token || stored.expiresAt !== current.expiresAt) {
      this.applySession(stored);
      return;
    }

    try {
      const result = await this.request<{
        token: string;
        expiresAt: string;
        user?: { id: string; account: string };
      }>('/auth/refresh', { method: 'POST' });

      // Older API builds return only { token, expiresAt }; keep the user.
      const user = result.user ?? current.user;
      this.persistSession({
        token: result.token,
        expiresAt: result.expiresAt,
        ...(user ? { user } : {})
      });
    } catch {
      // Session refresh failed, will expire naturally
    }
  }

  private async request<T>(path: string, options: ApiRequestOptions = {}, isRetry = false): Promise<T> {
    const headers: Record<string, string> = {};
    const method = options.method ?? 'GET';
    const sentToken = options.auth !== false ? this.session?.token : undefined;

    if (sentToken) {
      headers.authorization = `Bearer ${sentToken}`;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    const init: RequestInit = {
      method,
      headers,
      signal: controller.signal
    };

    if (options.body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }

    let response: Response;

    try {
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch (error) {
      clearTimeout(timeout);

      if (controller.signal.aborted) {
        throw new Error(`The API did not respond within ${REQUEST_TIMEOUT_MS / 1000} seconds (${path}).`);
      }

      if (error instanceof TypeError) {
        throw new Error(`Could not reach the API (${path}).`);
      }

      throw error;
    }

    try {
      if (!response.ok) {
        const text = await response.text();

        if (response.status === 401 && sentToken) {
          const handled = this.handleUnauthorized(sentToken);
          if (handled === 'retry' && !isRetry) {
            // The request was rejected before it ran, so replaying it with the
            // rotated token is safe.
            clearTimeout(timeout);
            return this.request<T>(path, options, true);
          }
        }

        throw new Error(extractErrorMessage(text) || `Request failed (${response.status})`);
      }

      if (method !== 'GET') {
        // Mutations can change credits (admin grants, runs, payments).
        this.billingSummaryCache = null;
      }

      return (await response.json()) as T;
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * A 401 only means *that* token is no longer valid. Clear the session only
   * when it is still the current one; otherwise a newer token (rotated by a
   * refresh in this or another tab) is already in place.
   */
  private handleUnauthorized(sentToken: string): 'retry' | 'cleared' | 'ignored' {
    if (this.session?.token && this.session.token !== sentToken) {
      return 'retry';
    }

    const stored = this.loadSession();
    if (stored?.token && stored.token !== sentToken) {
      this.applySession(stored);
      return 'retry';
    }

    if (this.session?.token === sentToken || stored?.token === sentToken) {
      this.persistSession(null);
      return 'cleared';
    }

    return 'ignored';
  }

  public async challenge(account: string) {
    return this.request<{ account: string; nonce: string; message: string; expiresAt: string }>('/auth/challenge', {
      method: 'POST',
      auth: false,
      body: {
        account
      }
    });
  }

  public async verify(account: string, message: string, signature: string) {
    const result = await this.request<{
      token: string;
      expiresAt: string;
      user: { id: string; account: string };
    }>('/auth/verify', {
      method: 'POST',
      auth: false,
      body: {
        account,
        message,
        signature
      }
    });

    this.persistSession({
      token: result.token,
      expiresAt: result.expiresAt,
      user: result.user
    });

    return result;
  }

  public async verifyHiveSigner(accessToken: string, username?: string) {
    const result = await this.request<{
      token: string;
      expiresAt: string;
      user: { id: string; account: string };
    }>('/auth/hivesigner', {
      method: 'POST',
      auth: false,
      body: {
        accessToken,
        username
      }
    });

    this.persistSession({
      token: result.token,
      expiresAt: result.expiresAt,
      user: result.user
    });

    return result;
  }

  public async me() {
    return this.request<{ user: { id: string; account: string } | null }>('/auth/me');
  }

  public async logout() {
    try {
      if (this.session?.token) {
        await this.request('/auth/logout', { method: 'POST' });
      }
    } finally {
      this.persistSession(null);
    }
  }

  public async listAutomations(cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (cursor) {
      params.set('cursor', cursor);
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>>; hasMore: boolean; nextCursor: string | null }>(`/automations${suffix}`);
  }

  public async getAutomation(id: string) {
    return this.request<{ item: Record<string, unknown> }>(`/automations/${id}`);
  }

  public async listAutomationVersions(id: string) {
    return this.request<{ items: Array<Record<string, unknown>> }>(`/automations/${id}/versions`);
  }

  public async cloneAutomation(id: string, payload?: { name?: string; enabled?: boolean }) {
    return this.request<{ item: Record<string, unknown> }>(`/automations/${id}/clone`, {
      method: 'POST',
      body: payload ?? {}
    });
  }

  public async createAutomation(input: CreateAutomationInput) {
    return this.request<{ item: Record<string, unknown> }>('/automations', {
      method: 'POST',
      body: input
    });
  }

  public async updateAutomation(id: string, input: Partial<AutomationDefinition>) {
    return this.request<{ item: Record<string, unknown> }>(`/automations/${id}`, {
      method: 'PATCH',
      body: input
    });
  }

  public async rotateAutomationWebhookKey(id: string, graceMinutes = 60) {
    return this.request<{
      webhookKey: string;
      previousWebhookKey: string | null;
      previousExpiresAt: string | null;
    }>(`/automations/${id}/webhook/rotate-key`, {
      method: 'POST',
      body: {
        graceMinutes
      }
    });
  }

  public async rollbackAutomation(id: string, version: number) {
    return this.request<{ item: Record<string, unknown> }>(`/automations/${id}/rollback`, {
      method: 'POST',
      body: { version }
    });
  }

  public async deleteAutomation(id: string) {
    return this.request<{ ok: boolean }>(`/automations/${id}`, {
      method: 'DELETE'
    });
  }

  public async getAutomationCatalog() {
    return this.request<{ catalog: Record<string, unknown> }>('/automations/catalog');
  }

  public async getAutomationTemplates() {
    return this.request<{ templates: Array<Record<string, unknown>> }>('/automations/templates');
  }

  public async simulateEvent(
    triggerType: string,
    payload: Record<string, unknown>,
    account?: string,
    automationId?: string
  ) {
    return this.request<{ queued: number; duplicate: boolean }>('/events/simulate', {
      method: 'POST',
      body: {
        triggerType,
        payload,
        account,
        automationId
      }
    });
  }

  public async recheckEvent(id: string, automationId?: string) {
    return this.request<{ eventId: string; enqueued: number; skippedExisting: number }>(
      `/events/${id}/recheck`,
      {
        method: 'POST',
        body: automationId ? { automationId } : {}
      }
    );
  }

  public async recheckUncaughtEvents(payload?: {
    limit?: number;
    sinceHours?: number;
    automationId?: string;
    triggerType?: string;
    dryRun?: boolean;
  }) {
    return this.request<{
      dryRun?: boolean;
      scannedEvents: number;
      enqueuedRuns: number;
      skippedExistingRuns: number;
      items: Array<Record<string, unknown>>;
    }>('/events/recheck-uncaught', {
      method: 'POST',
      body: payload ?? {}
    });
  }

  public async getFailedRunsSummary() {
    return this.request<{ items: Array<Record<string, unknown>> }>('/runs/failed/summary');
  }

  public async retryRunBatch(ids: string[]) {
    return this.request<{ ok: boolean; retried: number }>('/runs/retry-batch', {
      method: 'POST',
      body: { ids }
    });
  }

  public async listRuns(status?: string, cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (status) {
      params.set('status', status);
    }
    if (cursor) {
      params.set('cursor', cursor);
    }
    if (limit) {
      params.set('limit', String(limit));
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>>; hasMore: boolean; nextCursor: string | null }>(`/runs${suffix}`);
  }

  public async retryRun(id: string) {
    return this.request<{ ok: boolean }>(`/runs/${id}/retry`, {
      method: 'POST'
    });
  }

  public async getRun(id: string) {
    return this.request<{
      run: Record<string, unknown>;
      automation: Record<string, unknown> | null;
      event: Record<string, unknown> | null;
    }>(`/runs/${id}`);
  }

  /**
   * Billing summary is read by the shell (credit balance), overview and
   * billing pages. Concurrent and back-to-back callers share one request for
   * a short window; pass `fresh` to bypass the cache.
   */
  public getBillingSummary(options: { fresh?: boolean } = {}): Promise<BillingSummary> {
    const token = this.session?.token ?? '';
    const cached = this.billingSummaryCache;

    if (!options.fresh && cached && cached.token === token && Date.now() - cached.at < BILLING_SUMMARY_CACHE_MS) {
      return cached.promise;
    }

    const promise = this.request<BillingSummary>('/billing/summary');
    const entry = { token, at: Date.now(), promise };
    this.billingSummaryCache = entry;

    promise.catch(() => {
      if (this.billingSummaryCache === entry) {
        this.billingSummaryCache = null;
      }
    });

    return promise;
  }

  public async getAutomationAudit(cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (cursor) {
      params.set('cursor', cursor);
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>>; hasMore: boolean; nextCursor: string | null }>(`/automations/audit${suffix}`);
  }

  public async listBillingAdminUsers(search?: string, limit = 50) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (search?.trim()) {
      params.set('search', search.trim().toLowerCase());
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(`/billing/admin/users${suffix}`);
  }

  public async grantBillingAdminCredits(payload: { account: string; credits: number; note?: string }) {
    return this.request<{ ok: boolean; user: Record<string, unknown>; credits: Record<string, unknown> }>(
      '/billing/admin/grant',
      {
        method: 'POST',
        body: payload
      }
    );
  }

  public async adjustBillingAdminCredits(payload: {
    account: string;
    mode: 'grant' | 'debit' | 'set';
    credits: number;
    note?: string;
  }) {
    return this.request<{ ok: boolean; mode: string; user: Record<string, unknown>; credits: Record<string, unknown> }>(
      '/billing/admin/adjust',
      {
        method: 'POST',
        body: payload
      }
    );
  }

  public async listBillingAdminAudit(search?: string, limit = 100) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (search?.trim()) {
      params.set('search', search.trim().toLowerCase());
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(`/billing/admin/audit${suffix}`);
  }

  public async listKeys() {
    return this.request<{ items: Array<Record<string, unknown>> }>('/settings/keys');
  }

  public async saveKeys(payload: {
    label: string;
    account: string;
    postingKey?: string;
    activeKey?: string;
  }) {
    return this.request<{ item: Record<string, unknown> }>('/settings/keys', {
      method: 'POST',
      body: payload
    });
  }

  public async deleteKey(id: string) {
    return this.request<{ ok: boolean }>(`/settings/keys/${id}`, {
      method: 'DELETE'
    });
  }

  public async listSmtpCredentials() {
    return this.request<{ items: Array<Record<string, unknown>> }>('/settings/smtp');
  }

  public async saveSmtpCredential(payload: {
    label: string;
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    from: string;
    replyTo?: string;
  }) {
    return this.request<{ item: Record<string, unknown> }>('/settings/smtp', {
      method: 'POST',
      body: payload
    });
  }

  public async deleteSmtpCredential(id: string) {
    return this.request<{ ok: boolean }>(`/settings/smtp/${id}`, {
      method: 'DELETE'
    });
  }

  public async listTelegramCredentials() {
    return this.request<{ items: Array<Record<string, unknown>> }>('/settings/telegram');
  }

  public async saveTelegramCredential(payload: { label: string; botToken: string }) {
    return this.request<{ item: Record<string, unknown> }>('/settings/telegram', {
      method: 'POST',
      body: payload
    });
  }

  public async deleteTelegramCredential(id: string) {
    return this.request<{ ok: boolean }>(`/settings/telegram/${id}`, {
      method: 'DELETE'
    });
  }

  public async listSqlCredentials() {
    return this.request<{ items: Array<Record<string, unknown>> }>('/settings/sql');
  }

  public async saveSqlCredential(payload: {
    label: string;
    host: string;
    port: number;
    database: string;
    username: string;
    password: string;
    ssl: boolean;
  }) {
    return this.request<{ item: Record<string, unknown> }>('/settings/sql', {
      method: 'POST',
      body: payload
    });
  }

  public async deleteSqlCredential(id: string) {
    return this.request<{ ok: boolean }>(`/settings/sql/${id}`, {
      method: 'DELETE'
    });
  }

  public async listBlocks(type?: 'condition' | 'action') {
    const params = new URLSearchParams();
    if (type) {
      params.set('type', type);
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(`/blocks${suffix}`);
  }

  public async createBlock(payload: {
    type: 'condition' | 'action';
    name: string;
    description?: string;
    payload: Record<string, unknown>;
  }) {
    return this.request<{ item: Record<string, unknown> }>('/blocks', {
      method: 'POST',
      body: payload
    });
  }

  public async deleteBlock(id: string) {
    return this.request<{ ok: boolean }>(`/blocks/${id}`, {
      method: 'DELETE'
    });
  }

  public async listQueueMessages(name: string, options?: { status?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.status) {
      params.set('status', options.status);
    }

    if (options?.limit) {
      params.set('limit', String(options.limit));
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(
      `/queues/${encodeURIComponent(name)}/messages${suffix}`
    );
  }

  public async dequeueQueueMessages(name: string, payload?: { limit?: number; leaseSeconds?: number }) {
    return this.request<{ items: Array<Record<string, unknown>> }>(`/queues/${encodeURIComponent(name)}/dequeue`, {
      method: 'POST',
      body: payload ?? {}
    });
  }

  public async ackQueueMessage(id: string) {
    return this.request<{ ok: boolean }>(`/queues/messages/${id}/ack`, {
      method: 'POST',
      body: {}
    });
  }
}

export function extractErrorMessage(text: string): string {
  if (!text) {
    return '';
  }

  try {
    const parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
    // Fastify-style bodies put the generic status in `error` ("Validation
    // Error") and the useful detail in `message`, so prefer `message`.
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }

    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error;
    }

    return text;
  } catch {
    return text;
  }
}
